/**
 * Stable per-browser identifier sent as `device_id` on every device
 * registration.
 *
 * It identifies the BROWSER, not the user, so it must survive sign-out.
 * Losing it makes the next login register a second row for the same browser;
 * the old row keeps a token that still resolves, so every later send fans out
 * to duplicate addresses.
 */
const DEVICE_ID_KEY = 'orphix.admin.push.device_id';

const mintId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Pre-randomUUID Safari, and any non-secure origin.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const getDeviceId = () => {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const fresh = mintId();
    window.localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    // Private mode / storage blocked: a per-tab id still registers, it just
    // cannot be deduped across reloads.
    return mintId();
  }
};

/** Run a storage-clearing routine (sign-out) without losing the device id. */
export const preserveDeviceId = (clear) => {
  if (typeof window === 'undefined') return clear();
  let saved = null;
  try {
    saved = window.localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    /* storage blocked */
  }
  const result = clear();
  try {
    if (saved) window.localStorage.setItem(DEVICE_ID_KEY, saved);
  } catch {
    /* storage blocked */
  }
  return result;
};

export const getDeviceName = () => {
  if (typeof navigator === 'undefined') return 'Web';
  const ua = navigator.userAgent;

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';

  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Mac OS X/.test(ua)
      ? 'macOS'
      : /Android/.test(ua)
        ? 'Android'
        : /(iPhone|iPad|iPod)/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'Unknown OS';

  return `${browser} on ${os}`;
};
